/**
 * Topology Data Service
 * Fetches data from vector_db and builds the 5-level topology tree
 *
 * Sprint 33: Following OOA/OOD principles:
 * - Singleton Pattern: One data service instance
 * - Repository Pattern: Encapsulates all canister data access
 * - Builder Pattern: Builds tree from flat canister data
 */

import {
    TopologyNode,
    TopologyLevel,
    ProcessFlowData,
    ProcessStepData,
    ProcessFlowStatistics,
    BulkProcessFlowImport,
} from './topology-types';

/**
 * TopologyDataService - Singleton service for topology data operations
 */
export class TopologyDataService {
    private static instance: TopologyDataService;
    private canisterClient: any;

    private constructor() {
        this.canisterClient = (window as any).canisterClient;
    }

    public static getInstance(): TopologyDataService {
        if (!TopologyDataService.instance) {
            TopologyDataService.instance = new TopologyDataService();
        }
        return TopologyDataService.instance;
    }

    /**
     * Load the full topology tree from canister data
     * Levels 1-4 from business_capability_ops, Level 5 from process_flow_ops
     */
    public async loadTopologyTree(): Promise<TopologyNode> {
        const client = this.getClient();

        // Fetch levels 2-4 in parallel
        const [businessUnits, capabilities, platforms, assignments] = await Promise.all([
            client.callCanister('vector_db', 'list_business_units', []),
            client.callCanister('vector_db', 'list_business_capabilities', []),
            client.callCanister('vector_db', 'list_platforms', []),
            this.loadAllAssignments(client),
        ]);

        // Fetch level 5
        const processFlows = await client.callCanister('vector_db', 'list_process_flows', []);

        return this.buildTree(
            businessUnits || [],
            capabilities || [],
            platforms || [],
            assignments || [],
            processFlows || []
        );
    }

    /**
     * Load all capability assignments for all business units
     */
    private async loadAllAssignments(client: any): Promise<any[]> {
        const units = await client.callCanister('vector_db', 'list_business_units', []);
        if (!units || !Array.isArray(units)) return [];

        const allAssignments: any[] = [];
        for (const unit of units) {
            const buId = unit.business_unit_id;
            const assignments = await client.callCanister(
                'vector_db',
                'get_assignments_for_business_unit',
                [buId]
            );
            if (assignments && Array.isArray(assignments)) {
                allAssignments.push(...assignments);
            }
        }
        return allAssignments;
    }

    /**
     * Build the topology tree from flat canister data
     */
    private buildTree(
        businessUnits: any[],
        capabilities: any[],
        platforms: any[],
        assignments: any[],
        processFlows: any[]
    ): TopologyNode {
        // Build lookup maps
        const capMap = new Map<string, any>();
        for (const cap of capabilities) {
            capMap.set(cap.capability_id, cap);
        }

        const platMap = new Map<string, any>();
        for (const plat of platforms) {
            platMap.set(plat.platform_id, plat);
        }

        // Group assignments: bu_id -> cap_id -> platform_ids[]
        const assignmentMap = new Map<string, Map<string, string[]>>();
        for (const a of assignments) {
            if (!assignmentMap.has(a.business_unit_id)) {
                assignmentMap.set(a.business_unit_id, new Map());
            }
            const capAssignments = assignmentMap.get(a.business_unit_id)!;
            if (!capAssignments.has(a.capability_id)) {
                capAssignments.set(a.capability_id, []);
            }
            capAssignments.get(a.capability_id)!.push(a.platform_id);
        }

        // Group process flows: bu_id + cap_id -> flows[]
        const flowMap = new Map<string, any[]>();
        for (const flow of processFlows) {
            const key = `${flow.business_unit_id}__${flow.capability_id}`;
            if (!flowMap.has(key)) {
                flowMap.set(key, []);
            }
            flowMap.get(key)!.push(flow);
        }

        // Build tree: Bank -> BUs -> Capabilities -> Process Flows
        // (Platforms are stored as metadata, not as a tree level — matches Brett's design)
        const root: TopologyNode = {
            id: 'bank-root',
            label: 'Bank',
            level: 1,
            type: 'bank',
            children: [],
            metadata: {},
            expanded: true,
        };

        for (const bu of businessUnits) {
            const buNode: TopologyNode = {
                id: bu.business_unit_id,
                label: bu.name,
                level: 2,
                type: 'business-unit',
                children: [],
                metadata: this.parseMetadata(bu.metadata),
                expanded: false,
            };

            // Collect all capability IDs for this BU from both assignments and process flows
            const capIdsForBu = new Set<string>();

            const capAssignments = assignmentMap.get(bu.business_unit_id);
            if (capAssignments) {
                for (const capId of capAssignments.keys()) {
                    capIdsForBu.add(capId);
                }
            }

            // Also discover capabilities from process flows (covers flows without assignments)
            for (const [key, _flows] of flowMap) {
                const [buId, capId] = key.split('__');
                if (buId === bu.business_unit_id && capId) {
                    capIdsForBu.add(capId);
                }
            }

            for (const capId of capIdsForBu) {
                const cap = capMap.get(capId);
                if (!cap) continue;

                const capNode: TopologyNode = {
                    id: `${bu.business_unit_id}__${capId}`,
                    label: cap.name,
                    level: 3,
                    type: 'capability',
                    children: [],
                    metadata: this.parseMetadata(cap.metadata),
                    expanded: false,
                };

                // Add process flows under capability, grouped by sub_capability
                const flowKey = `${bu.business_unit_id}__${capId}`;
                const flows = flowMap.get(flowKey) || [];
                const addedFlowIds = new Set<string>();

                // Build flow nodes first
                const flowNodes: TopologyNode[] = [];
                for (const flow of flows) {
                    if (addedFlowIds.has(flow.process_flow_id)) continue;
                    addedFlowIds.add(flow.process_flow_id);

                    const flowPlatId = this.extractOpt(flow.platform_id);
                    const platformName = flowPlatId ? (platMap.get(flowPlatId)?.name || flowPlatId) : '';

                    const flowNode: TopologyNode = {
                        id: flow.process_flow_id,
                        label: flow.name,
                        level: 4 as TopologyLevel,
                        type: 'process-flow',
                        children: [],
                        metadata: {
                            ...this.parseMetadata(flow.metadata),
                            status: this.extractVariant(flow.status),
                            owner_role: flow.owner_role || '',
                            description: flow.description || '',
                            platform: platformName,
                        },
                        expanded: false,
                    };
                    flowNodes.push(flowNode);
                }

                // Group by sub_capability if multiple distinct values exist
                const subCapGroups = new Map<string, TopologyNode[]>();
                for (const fn of flowNodes) {
                    const subCap = fn.metadata.sub_capability || '';
                    if (!subCapGroups.has(subCap)) subCapGroups.set(subCap, []);
                    subCapGroups.get(subCap)!.push(fn);
                }

                if (subCapGroups.size > 1) {
                    // Insert grouping nodes between capability and flows
                    for (const [subCapName, groupFlows] of subCapGroups) {
                        const groupNode: TopologyNode = {
                            id: `${bu.business_unit_id}__${capId}__${subCapName.toLowerCase().replace(/\s+/g, '-')}`,
                            label: subCapName || 'General',
                            level: 4 as TopologyLevel,
                            type: 'capability',
                            children: groupFlows,
                            metadata: { group_type: 'sub-capability' },
                            expanded: false,
                        };
                        capNode.children.push(groupNode);
                    }
                } else {
                    // Single or no sub-capability — add flows directly
                    for (const fn of flowNodes) {
                        capNode.children.push(fn);
                    }
                }

                buNode.children.push(capNode);
            }

            root.children.push(buNode);
        }

        return root;
    }

    /**
     * Get process flows by capability
     */
    public async getProcessFlows(buId: string, capId: string): Promise<ProcessFlowData[]> {
        const client = this.getClient();
        const result = await client.callCanister(
            'vector_db',
            'get_process_flows_by_capability',
            [buId, capId]
        );

        if (!result || !Array.isArray(result)) return [];

        return result.map((flow: any) => ({
            processFlowId: flow.process_flow_id,
            name: flow.name,
            description: flow.description,
            businessUnitId: flow.business_unit_id,
            capabilityId: flow.capability_id,
            platformId: this.extractOpt(flow.platform_id),
            status: this.extractVariant(flow.status) as any,
            ownerRole: flow.owner_role,
            totalSlaHours: this.extractOptNumber(flow.total_sla_hours),
            metadata: this.parseMetadata(flow.metadata),
        }));
    }

    /**
     * Get process steps for a flow
     */
    public async getProcessSteps(flowId: string): Promise<ProcessStepData[]> {
        const client = this.getClient();
        const result = await client.callCanister(
            'vector_db',
            'get_process_steps',
            [flowId]
        );

        if (!result || !Array.isArray(result)) return [];

        return result.map((step: any) => ({
            stepId: step.step_id,
            processFlowId: step.process_flow_id,
            stepNumber: step.step_number,
            name: step.name,
            description: step.description,
            stepType: this.extractVariant(step.step_type) as any,
            role: step.role,
            system: this.extractOpt(step.system),
            infrastructure: step.infrastructure || [],
            slaHours: this.extractOptNumber(step.sla_hours),
            nextSteps: step.next_steps || [],
            decisionOptions: step.decision_options || [],
            metadata: this.parseMetadata(step.metadata),
        }));
    }

    /**
     * Get process flow statistics
     */
    public async getStatistics(): Promise<ProcessFlowStatistics> {
        const client = this.getClient();
        return await client.callCanister('vector_db', 'get_process_flow_statistics', []);
    }

    /**
     * Bulk import process flows and steps (seed data)
     */
    public async bulkImport(data: BulkProcessFlowImport): Promise<any> {
        const client = this.getClient();
        return await client.callCanister('vector_db', 'bulk_import_process_flows', [data]);
    }

    /**
     * Bulk import capability model (BUs, capabilities, platforms, assignments)
     */
    public async bulkImportCapabilities(data: any): Promise<any> {
        const client = this.getClient();
        return await client.callCanister('vector_db', 'bulk_import_business_capabilities', [data]);
    }

    /**
     * Seed bank data from the seed data module.
     * Seeds capability model first (BUs, capabilities, platforms, assignments),
     * then process flows and steps.
     */
    public async seedBankData(): Promise<any> {
        const seedModule = await import('./topology-seed-data');

        // Phase 1: Seed the capability model (BUs, capabilities, platforms, assignments)
        if (seedModule.generateCapabilityModel) {
            const capModel = seedModule.generateCapabilityModel();
            const capResult = await this.bulkImportCapabilities(capModel);
            console.log('Capability model seed result:', capResult);
        }

        // Phase 2: Seed process flows and steps
        const seedData = seedModule.generateSeedData();
        return await this.bulkImport(seedData);
    }

    // ======================
    // Helper Methods
    // ======================

    private getClient(): any {
        if (!this.canisterClient) {
            this.canisterClient = (window as any).canisterClient;
        }
        if (!this.canisterClient) {
            throw new Error('CanisterClient not available');
        }
        return this.canisterClient;
    }

    private parseMetadata(metadata: any): Record<string, string> {
        if (!metadata) return {};
        if (!Array.isArray(metadata)) return metadata as Record<string, string>;
        const result: Record<string, string> = {};
        for (const item of metadata) {
            if (Array.isArray(item) && item.length >= 2) {
                result[item[0]] = item[1];
            }
        }
        return result;
    }

    /**
     * Extract value from Candid opt type: [] = None, [value] = Some(value)
     */
    private extractOpt(opt: any): string | undefined {
        if (Array.isArray(opt)) {
            return opt.length > 0 ? opt[0] : undefined;
        }
        return opt || undefined;
    }

    private extractOptNumber(opt: any): number | undefined {
        if (Array.isArray(opt)) {
            return opt.length > 0 ? Number(opt[0]) : undefined;
        }
        return opt !== undefined && opt !== null ? Number(opt) : undefined;
    }

    /**
     * Extract variant name from Candid variant: { Draft: null } -> "Draft"
     */
    private extractVariant(variant: any): string {
        if (typeof variant === 'string') return variant;
        if (variant && typeof variant === 'object') {
            const keys = Object.keys(variant);
            return keys.length > 0 ? keys[0] : 'Unknown';
        }
        return 'Unknown';
    }
}
